import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/core/components/ui/button';
import { ChevronDown, Columns, Eye, EyeOff } from 'lucide-react';
import { VisibilityState } from '@tanstack/react-table';

interface ColumnFilterProps {
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
  availableColumns: Array<{
    id: string;
    header: string;
  }>;
}

export const ColumnFilter = ({
  columnVisibility,
  onColumnVisibilityChange,
  availableColumns
}: ColumnFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.right - 256, // 256px = w-64 (dropdown width)
        width: rect.width
      });
    }
  }, [isOpen]);

  const visibleCount = availableColumns.filter(col => 
    columnVisibility[col.id] !== false
  ).length;

  const toggleColumn = (columnId: string) => {
    const newVisibility = {
      ...columnVisibility,
      [columnId]: !columnVisibility[columnId]
    };
    onColumnVisibilityChange(newVisibility);
  };

  const showAllColumns = () => {
    const allVisible: VisibilityState = {};
    availableColumns.forEach(col => {
      allVisible[col.id] = true;
    });
    onColumnVisibilityChange(allVisible);
  };

  const hideAllColumns = () => {
    const allHidden: VisibilityState = {};
    availableColumns.forEach(col => {
      allHidden[col.id] = false;
    });
    onColumnVisibilityChange(allHidden);
  };

  return (
    <div ref={buttonRef} className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-sm"
      >
        <Columns className="h-4 w-4" />
        <span>Columns ({visibleCount}/{availableColumns.length})</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div 
            className="fixed w-64 bg-white border rounded-lg shadow-lg z-50"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`
            }}
          >
            <div className="p-3 border-b">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">Column Visibility</span>
                <span className="text-xs text-gray-500">
                  {visibleCount} of {availableColumns.length} visible
                </span>
              </div>
              
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={showAllColumns}
                  className="text-xs flex-1"
                >
                  Show All
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={hideAllColumns}
                  className="text-xs flex-1"
                >
                  Hide All
                </Button>
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {availableColumns.map((column) => {
                const isVisible = columnVisibility[column.id] !== false;
                
                return (
                  <div
                    key={column.id}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleColumn(column.id)}
                  >
                    <div className="flex items-center space-x-2">
                      {isVisible ? (
                        <Eye className="h-4 w-4 text-blue-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                      <span className={`text-sm ${isVisible ? 'text-gray-900' : 'text-gray-500'}`}>
                        {column.header}
                      </span>
                    </div>
                    
                    <div className={`w-4 h-4 rounded border-2 ${
                      isVisible 
                        ? 'bg-blue-600 border-blue-600' 
                        : 'border-gray-300'
                    } flex items-center justify-center`}>
                      {isVisible && (
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 8 8">
                          <path d="M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z"/>
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};